import { Link, Tag, Text } from '@billy/design-system'
import { TFunction } from 'i18next'
import orderBy from 'lodash/orderBy'
import { ReactNode, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { InvoiceDocumentType } from '@features/invoices/enums/invoiceDocumentType'
import type { Invoice } from '@features/invoices/types/invoices/invoice'
import { getInvoiceDocumentRouteBase } from '@features/invoices/utils/getInvoiceDocumentRouteBase'
import { getInvoiceDocumentTitle } from '@features/invoices/utils/getInvoiceDocumentTitle'
import { RecurringInvoiceDelivery } from '@features/recurringInvoices/enums/recurringInvoiceDelivery'
import { RecurringInvoiceEventType } from '@features/recurringInvoices/enums/recurringInvoiceEventType'
import type { RecurringInvoice } from '@features/recurringInvoices/types/recurringInvoice'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { getOrganizationViewUrl } from '../../../../../../../../utils/routing/getOrganizationViewUrl'
import { reactRoute } from '../../../../../../../../utils/routing/reactRoute'
import { useRecurringInvoice } from '../../../../contexts/recurringInvoiceContext'
import type { GeneratedDocument } from '../../types/generatedDocument'
import { RecurringInvoiceTimeline } from '../RecurringInvoiceTimeline'

const getInvoiceNumber = (invoice: Invoice) =>
  invoice?.invoiceNumber
    ? invoice?.invoiceNumber
    : [invoice.config.invoiceNumber.prefix, invoice.config.invoiceNumber.number].filter(Boolean).join('-')

interface GetCreatedDocumentsListOptions {
  documents?: Invoice[]
  isDraft?: boolean
  t: TFunction
}

const getCreatedDocumentsList = ({ documents = [], isDraft = false, t }: GetCreatedDocumentsListOptions) =>
  documents.map((document: Invoice) => {
    return {
      issueDate: document.issueDate,
      id: String(document.id),
      title: getInvoiceDocumentTitle({
        documentType: InvoiceDocumentType.Invoice,
        language: document.locale,
        t,
      }),
      isDraft,
      ...(isDraft ? {} : { invoiceNumber: getInvoiceNumber(document) }),
    }
  })

const getDisplayDocumentsCount = (recurringInvoice: RecurringInvoice) => {
  const { invoiceDrafts, invoices, schedule, status } = recurringInvoice
  const isCreateDraft = schedule.action === RecurringInvoiceDelivery.CreateDraft

  const documentsList = isCreateDraft ? invoiceDrafts : invoices

  // drafts can be approved, deleted etc. and disappear from the list,
  // so we're counting events, not documents - to be revisited
  const documentsGeneratedCount = isCreateDraft
    ? status.events.filter(({ eventType }) => eventType === RecurringInvoiceEventType.ProfileDocumentDraftCreated)
        .length
    : documentsList.length
  const documentsScheduledCount = schedule.repeat

  return documentsScheduledCount === null
    ? documentsGeneratedCount
    : [documentsGeneratedCount, documentsScheduledCount].join('/')
}

export const RecurringInvoiceGeneratedDocuments = () => {
  const { t } = useTranslation()
  const { organization, isLoading: isLoadingOrganization } = useUserOrganization()
  const { recurringInvoice, isLoading: isLoadingRecurringInvoice } = useRecurringInvoice()

  const isLoading = isLoadingOrganization || isLoadingRecurringInvoice

  const documentsCreated = useMemo(() => {
    const invoicesCreated = getCreatedDocumentsList({ documents: recurringInvoice?.invoices, t })
    const draftsCreated = getCreatedDocumentsList({ documents: recurringInvoice?.invoiceDrafts, t, isDraft: true })

    return orderBy([...invoicesCreated, ...draftsCreated], 'issueDate', 'desc')
  }, [recurringInvoice?.invoiceDrafts, recurringInvoice?.invoices, t])

  const documentsCountTag = useMemo(() => {
    if (isLoading || !recurringInvoice) {
      return undefined
    }

    const displayedDocumentsCount = getDisplayDocumentsCount(recurringInvoice)

    return (
      <Tag color="neutral" size="sm" type="subtle">
        {displayedDocumentsCount}
      </Tag>
    )
  }, [isLoading, recurringInvoice])

  const getTimelineStatusItemTitle = useCallback(
    (item: GeneratedDocument): ReactNode => {
      const linkText = !item.isDraft && item.invoiceNumber ? `${item?.title}-${item.invoiceNumber}` : item?.title
      const route = item.isDraft
        ? reactRoute.organization.invoices.getEditDocumentDraftRoute(
            getInvoiceDocumentRouteBase(InvoiceDocumentType.InvoiceDraft),
            item.id,
          )
        : reactRoute.organization.invoices.getViewInvoiceRoute(item.id, InvoiceDocumentType.Invoice)
      const invoiceLink = `${getOrganizationViewUrl(organization?.url, route)}`
      return (
        <>
          <Link to={invoiceLink} underlined>
            {linkText}
          </Link>{' '}
          {item.isDraft && (
            <Tag color="purple" size="xs" type="subtle">
              {t('recurring_invoices.page.invoices_created.list.item.draft.badge.label')}
            </Tag>
          )}
        </>
      )
    },
    [organization?.url, t],
  )

  const getTimelineItemDate = useCallback((item: GeneratedDocument): string => item.issueDate, [])

  return (
    <RecurringInvoiceTimeline
      dateOnly
      getItemDate={getTimelineItemDate}
      getItemTitle={getTimelineStatusItemTitle}
      isLoading={isLoading}
      items={documentsCreated || []}
      title={t('recurring_invoices.page.invoices_created.title')}
      titleSuffix={documentsCountTag}
      withSeparator={false}
      withStatus={false}
    >
      <Text variant="bodyMD" colorVariant="discreet">
        {t('recurring_invoices.page.invoices_created.list.no_invoices_created')}
      </Text>
    </RecurringInvoiceTimeline>
  )
}
